import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Handbag, User, SignOut, ShieldCheck, List, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useConfig } from "@/hooks/useConfig";

const NAV = [
  { to: "/#poems", label: "Poems", testid: "poems" },
  { to: "/#shelf", label: "The shelf", testid: "shelf" },
  { to: "/#about", label: "The poet", testid: "about" },
  { to: "/contact", label: "Contact us", testid: "contact" },
  { to: "/terms", label: "Terms", testid: "terms" },
  { to: "/refund-policy", label: "Refunds", testid: "refund" },
];

export function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [location.pathname, location.hash]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-200 hover:text-primary ${isActive ? "text-primary" : "text-foreground/75"}`;

  const signOut = async () => { await logout(); setOpen(false); navigate("/"); };

  return (
    <header className="glass-nav sticky top-0 z-40 border-b border-foreground/10" data-testid="site-header">
      <div className="mx-auto flex h-[70px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Link to="/" className="group flex items-baseline gap-2.5" data-testid="header-logo-link">
          <span className="font-display text-xl font-semibold tracking-tight sm:text-[1.4rem]">{config?.store_name || "ShansoBooks"}</span>
          <span className="hidden font-display text-sm text-primary/70 transition-colors group-hover:text-primary sm:inline">அகமலி</span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex lg:gap-8">
          {NAV.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.testid}-link`}>{l.label}</NavLink>
          ))}
          {user && <NavLink to="/account" className={linkCls} data-testid="nav-account-link">My orders</NavLink>}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/admin"
            className="hidden items-center gap-1.5 rounded-sm border border-foreground/15 px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-foreground/70 transition-colors duration-200 hover:border-primary hover:text-primary md:flex"
            data-testid="admin-button"
          >
            <ShieldCheck size={15} weight="duotone" /> Admin
          </Link>
          {user ? (
            <button onClick={signOut} className="hidden items-center gap-1.5 rounded-sm px-3 py-2 text-sm text-foreground/75 transition-colors hover:bg-muted md:flex" data-testid="logout-button">
              <SignOut size={18} /> <span className="max-w-[110px] truncate">{user.name?.split(" ")[0]}</span>
            </button>
          ) : (
            <Link to="/login" className="hidden items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-medium text-foreground/75 transition-colors hover:bg-muted md:flex" data-testid="login-link">
              <User size={18} /> Sign in
            </Link>
          )}
          <Link to="/cart" className="relative flex h-10 w-10 items-center justify-center rounded-sm bg-foreground text-white transition-colors duration-200 hover:bg-primary" data-testid="cart-link">
            <Handbag weight="duotone" size={19} />
            {count > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold text-white ring-2 ring-background" data-testid="cart-count-badge">{count}</span>
            )}
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-sm border border-foreground/15 text-foreground transition-colors hover:border-primary hover:text-primary md:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-button"
          >
            {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              className="fixed inset-x-0 bottom-0 top-[70px] z-30 bg-foreground/25 md:hidden"
              data-testid="mobile-menu-overlay"
            />
            <motion.nav
              initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 right-0 top-full z-40 border-b border-foreground/10 bg-background px-5 pb-7 pt-3 shadow-xl md:hidden"
              data-testid="mobile-menu"
            >
              <ul className="divide-y divide-foreground/10">
                {NAV.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} onClick={() => setOpen(false)} className="block py-3.5 font-display text-lg text-foreground transition-colors hover:text-primary" data-testid={`mobile-nav-${l.testid}-link`}>{l.label}</Link>
                  </li>
                ))}
                {user && (
                  <li><Link to="/account" onClick={() => setOpen(false)} className="block py-3.5 font-display text-lg transition-colors hover:text-primary" data-testid="mobile-nav-account-link">My orders</Link></li>
                )}
              </ul>
              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                {user ? (
                  <button onClick={signOut} className="inline-flex items-center gap-1.5 rounded-sm border border-foreground/15 px-4 py-2.5 text-sm font-semibold transition-colors hover:border-primary hover:text-primary" data-testid="mobile-logout-button">
                    <SignOut size={16} /> Sign out
                  </button>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)} className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-[#702F2A]" data-testid="mobile-login-link">
                    <User size={16} /> Sign in
                  </Link>
                )}
                <Link to="/admin" onClick={() => setOpen(false)} className="inline-flex items-center gap-1.5 rounded-sm border border-foreground/15 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground/70 transition-colors hover:border-primary hover:text-primary" data-testid="mobile-admin-button">
                  <ShieldCheck size={15} weight="duotone" /> Admin
                </Link>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
